
<template>
	<v-container fluid>
		<v-snackbar v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
			{{ snackbar.message }}
			<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false"> Close </v-btn>
		</v-snackbar>

		<v-alert v-if="isComplete" class="text-xs-left mb-3" outline :value="true" type="success" icon="check"> Check in was completed <AppDate :date="checkIn.updatedDateUtc" /> </v-alert>

		<v-layout row>
			<v-flex lg7 grow pa-1>
				<v-card class="mb-4">
					<v-toolbar flat dense cad>
						<v-toolbar-title class="subheading">Questions</v-toolbar-title>
						<v-spacer></v-spacer>
						<v-btn icon flat @click="print">
							<v-badge color="red" overlap>
								<v-icon medium>print</v-icon>
							</v-badge>
						</v-btn>
					</v-toolbar>
					<v-divider></v-divider>
					<v-card-text>
						<v-card flat class="question-card" v-for="(item, index) in form.reviewItems" :key="`discussion-item-${index}`">
							<v-card-text>
								<h3 class="question-card-title mb-2">{{ item.question }}</h3>

								<div v-if="item.rankType == 0">
									<v-textarea
										v-if="isEmployeeUnderReview && !isComplete"
										class="item-textarea"
										style="margin-bottom: -10px"
										:rows="2"
										auto-grow
										:placeholder="textPlaceholder"
										v-model="item.answer"
										solo
										:readonly="!canEdit"
									></v-textarea>
									<div class="answer" v-else-if="item.answer == null || item.answer == ''">{{ textPlaceholder }}</div>
									<div class="answer" v-else>{{ item.answer }}</div>
								</div>

								<div v-else-if="item.rankType == 1">
									<span class="item tl-red is-not-selected" :class="setClass(item, 0)" @click="setAnswer(item, 0)">
										<v-icon>mdi-circle</v-icon>
									</span>
									<span class="item tl-amber is-not-selected" :class="setClass(item, 1)" @click="setAnswer(item, 1)">
										<v-icon>mdi-circle</v-icon>
									</span>
									<span class="item tl-green is-not-selected" :class="setClass(item, 2)" @click="setAnswer(item, 2)">
										<v-icon>mdi-circle</v-icon>
									</span>
									<div v-if="item.answer != null" class="mt-2">
										<div v-if="isComplete && item.furtherComment">
											<div class="answer">{{ item.furtherComment }}</div>
										</div>
										<div v-else-if="!isComplete">
											<a v-if="!item.showFurtherComment && !item.furtherComment" @click="showFurtherComment(item.id)">{{ getFurtherCommentLinkText(item) }}</a>

											<div v-else>
												<span class="further-comment-text">{{ getFurtherCommentLinkText(item) }}</span>

												<v-textarea
													:id="`furtherComment${item.id}`"
													class="item-textarea mt-3"
													style="margin-bottom: -10px"
													auto-grow
													:rows="2"
													placeholder="Optional answer..."
													v-model="item.furtherComment"
													solo
													:readonly="!canEdit"
												></v-textarea>
											</div>
										</div>
									</div>
								</div>

								<div v-else-if="item.rankType == 2" :disabled="!canEdit">
									<span class="item very-sad is-not-selected" :class="setClass(item, 0)" @click="setAnswer(item, 0)">
										<v-icon>mood_bad</v-icon>
									</span>
									<span class="item sad is-not-selected" :class="setClass(item, 1)" @click="setAnswer(item, 1)">
										<v-icon>mdi-emoticon-sad-outline</v-icon>
									</span>
									<span class="item neutral is-not-selected" :class="setClass(item, 2)" @click="setAnswer(item, 2)">
										<v-icon>mdi-emoticon-neutral-outline</v-icon>
									</span>
									<span class="item happy is-not-selected" :class="setClass(item, 3)" @click="setAnswer(item, 3)">
										<v-icon>mdi-emoticon-happy-outline</v-icon>
									</span>
									<span class="item very-happy is-not-selected" :class="setClass(item, 4)" @click="setAnswer(item, 4)">
										<v-icon>mood</v-icon>
									</span>
									<div v-if="item.answer != null" class="mt-2">
										<div v-if="isComplete && item.furtherComment">
											<div class="answer">{{ item.furtherComment }}</div>
										</div>
										<div v-else-if="!isComplete">
											<a v-if="!item.showFurtherComment && !item.furtherComment" @click="showFurtherComment(item.id)">{{ getFurtherCommentLinkText(item) }}</a>

											<div v-else>
												<span class="further-comment-text">{{ getFurtherCommentLinkText(item) }}</span>

												<v-textarea
													:id="`furtherComment${item.id}`"
													class="item-textarea mt-3"
													style="margin-bottom: -10px"
													auto-grow
													:rows="2"
													placeholder="Optional answer..."
													v-model="item.furtherComment"
													solo
													:readonly="!canEdit"
												></v-textarea>
											</div>
										</div>
									</div>
								</div>

								<div v-else>
									<span class="not-answered-text">Uh oh! Couldn't get this answer.</span>
								</div>
							</v-card-text>
						</v-card>
					</v-card-text>
				</v-card>
			</v-flex>
			<v-flex lg5 grow pa-1>
				<v-card>
					<v-toolbar flat dense cad class="info" dark>
						<v-toolbar-title class="subheading">Check In Detail</v-toolbar-title>
					</v-toolbar>
					<v-divider></v-divider>

					<v-list two-line>
						<v-list-tile inactive>
							<v-list-tile-action>
								<v-avatar size="36px">
									<img :src="getAvatar(checkIn.employee.id)" alt="avatar" class="avatar-img" />
								</v-avatar>
							</v-list-tile-action>
							<v-list-tile-content>
								<v-list-tile-title>{{ checkIn.employee.fullName }}</v-list-tile-title>
								<v-list-tile-sub-title>{{ $term('employee', { case: 'title' }) }}</v-list-tile-sub-title>
							</v-list-tile-content>
						</v-list-tile>

						<v-divider inset></v-divider>

						<v-list-tile inactive>
							<v-list-tile-action>
								<v-avatar size="36px">
									<img :src="getAvatar(checkIn.reviewer.id)" alt="avatar" class="avatar-img" />
								</v-avatar>
							</v-list-tile-action>
							<v-list-tile-content>
								<v-list-tile-title>{{ checkIn.reviewer.fullName }}</v-list-tile-title>
								<v-list-tile-sub-title>Reviewer</v-list-tile-sub-title>
							</v-list-tile-content>
						</v-list-tile>

						<v-list-tile inactive>
							<v-list-tile-action>
								<v-icon color="indigo">date_range</v-icon>
							</v-list-tile-action>

							<v-list-tile-content>
								<v-list-tile-title v-if="checkIn.dueDateUtc">{{ checkIn.dueDateUtc | formatDate() }}</v-list-tile-title>
								<v-list-tile-title v-else>N/A</v-list-tile-title>
								<v-list-tile-sub-title>Due Date</v-list-tile-sub-title>
							</v-list-tile-content>
						</v-list-tile>
					</v-list>
				</v-card>

				<comments-list
					class="mt-3"
					ref="commentsList"
					:comments="checkIn.comments"
					:loggedInEmployee="loggedInEmployee"
					:is-saving-comment="isSavingComment"
					:deleting-comment-id="deletingCommentId"
					@onSave="onSaveComment"
					@onDelete="onDeleteComment"
				/>
			</v-flex>
		</v-layout>
		<v-layout row>
			<v-flex xs10 shrink>
				<v-footer class="noPrint" color app height="55">
					<v-spacer></v-spacer>
					<span style="margin-right: 10px" :class="{ 'items-remaining': !isReadyToSubmit }">{{ itemsRemainingString }}</span>
					<footer-back-btn @click="onCancel" />
					<v-btn v-if="canSave" :loading="isSaving" :disabled="isSaving" @click="submitForm(false)" color="primary"
						>Save
						<span slot="loader">Saving...</span>
					</v-btn>
					<v-btn v-if="canSaveAndSubmit" :loading="isSubmitting" :disabled="isSubmitting" class="action-btn" @click="submitForm(true)"
						>Save & Submit
						<span slot="loader">Saving...</span>
					</v-btn>
				</v-footer>
			</v-flex>
		</v-layout>
	</v-container>
</template>

<script>
	import CheckInItemView from '@/components/checkin/CheckInItemView';
	import CommentsList from '@/components/common/CommentsList';
	import avatarMixin from '@/mixins/avatarMixin';
	import { validationMixin } from 'vuelidate';
	import { required } from 'vuelidate/lib/validators';

	export default {
		props: {
			checkIn: {
				required: true
			},
			loggedInEmployee: {
				type: Object,
				required: true
			},
			isSaving: {
				required: true,
				type: Boolean
			},
			isSubmitting: {
				required: true,
				type: Boolean
			}
		},
		components: {
			CheckInItemView,
			CommentsList,
		},
		data() {
			return {
				form: {},
				newComment: '',
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink'
				},
				isSavingComment: false,
				deletingCommentId: null,
			};
		},
		mixins: [avatarMixin, validationMixin],
		validations: {
			newComment: { required }
		},
		created() {
			this.form = Object.assign({}, this.checkIn);
		},
		methods: {
			setClass(item, expectedAnswer) {
				var classes = [];
				if (this.checkIn.employee.id !== this.loggedInEmployee.id) {
					classes.push('mouse-disabled-input');

					if (item.answer === null) {
						classes.push('is-not-selected');
					}
					else {
						classes.push(Number(item.answer) === expectedAnswer ? 'is-selected' : 'is-not-selected');
					}			

					return classes;
				}

				if (this.isComplete) {
					classes.push('mouse-disabled-input');
				} else {
					classes.push('grow-on-hover');
				}
				
				if (item.answer === null) {
					return classes;
				}

				classes.push(Number(item.answer) === expectedAnswer ? 'is-selected' : 'is-not-selected');
				return classes;
			},
			setAnswer(item, answer) {
				if(!this.isComplete) {
					if (this.checkIn.employee.id === this.loggedInEmployee.id) {
						item.answer = answer;

						if (item.showFurtherComment)
							this.focusFurtherCommentInput(item);

					}
				}			
			},
			getClass(result) {
				switch (result) {
					case 0:
						return 'error';
					case 1:
						return 'warning';
					case 2:
						return 'success';
				}
			},
			getHappyIcon(result) {
				switch (result) {
					case 0:
						return 'mdi-emoticon-cry-outline';
					case 1:
						return 'mdi-emoticon-sad-outline';
					case 2:
						return 'mdi-emoticon-neutral-outline';
					case 3:
						return 'mdi-emoticon-happy-outline';
					case 4:
						return 'mdi-emoticon-outline';
					default:
						return '';
				}
			},
			print() {
				window.print();
			},
			onCancel() {
				this.$emit('onCancel');
			},
			submitForm(signOff) {
				this.form.signOff = signOff;

				this.$emit('save', this.form);
			},
			async onSaveComment(comment) {
				this.isSavingComment = true;

				var payload = {
					id: comment.id,
					author: comment.author,
					text: comment.text,
					checkInId: this.checkIn.id
				};

				var newComment = await this.$store.dispatch('checkIns/addComment', payload);

				this.isSavingComment = false;
				this.$refs.commentsList.resetForm();

				if (newComment) {
					this.showSnackbar('Comment saved');
				} else {
					this.showSnackbar('Failed to save Comment', true);
				}
			},
			async onDeleteComment(comment) {
				this.deletingCommentId = comment.id;

				var payload = {
					id: comment.id,
					checkInId: this.checkIn.id
				};

				try {
					await this.$store.dispatch('checkIns/deleteComment', payload);
					this.deletingCommentId = null;
					this.showSnackbar('Comment deleted');
				} catch (error) {
					this.deletingCommentId = null;
					this.showSnackbar('Failed to delete Comment', true);
				}
			},
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000;
			},
			showFurtherComment(itemId) {
				const foundIndex = this.form.reviewItems.findIndex(x => x.id === itemId);
				if (foundIndex > -1) {
					const updated = { ...this.form.reviewItems[foundIndex], showFurtherComment: true };
					this.form.reviewItems.splice(foundIndex, 1, updated);
					
					this.focusFurtherCommentInput(this.form.reviewItems[foundIndex]);
				}
			},
			focusFurtherCommentInput(item) {
				const inputRef = `furtherComment${item.id}`;
				setTimeout(() => document.getElementById(inputRef).focus(), 50);
			},
			getFurtherCommentLinkText(item) {
				const defaultText = "Anything to add?";
				const questionsForCustomAnswers = [
					"How did you feel at work this week?",
					"How happy have you been with your job since the last check in?"
					];
					
				if (item.rankType == 2 && questionsForCustomAnswers.includes(item.question)) {
					switch (Number(item.answer)) {
						case 0:
							return "Sorry to hear that. Anything you need?";
						case 1:
							return "Is there anything blocking you that you'd like addressed?";
						case 2:
							return defaultText;
						case 3:
							return "Smooth and steady! Anything to add?";
						case 4:
							return "That's great to hear! Anything to add?";
						default: defaultText;
						}				
				} else {
					return defaultText;
				}
			}
		},
		computed: {
			canSave() {
				return this.loggedInEmployee.id === this.checkIn.employee.id && !this.isComplete;
			},
			canSaveAndSubmit() {
				return this.loggedInEmployee.id === this.checkIn.employee.id && !this.isComplete && this.isReadyToSubmit;
			},
			canEdit() {
				return this.isEmployeeUnderReview && !this.isComplete;
			},
			isComplete() {
				return this.checkIn.status === "Complete";
			},
			newCommentErrors() {
				const errors = [];
				if (!this.$v.newComment.$dirty) return errors;
				!this.$v.newComment.required && errors.push('Comment cannot be empty.');
				return errors;
			},
			textPlaceholder() {
				if (this.checkIn.employee.id === this.loggedInEmployee.id) {
					return 'Type your answer here.';
				} else {
					return 'Not yet answered.';
				}
			},
			isEmployeeUnderReview() {
				return this.loggedInEmployee.id === this.checkIn.employee.id;
			},
			isReadyToSubmit() {
				return this.form && this.form.reviewItems && this.form.reviewItems.every(x => x.answer !== null && x.answer !== "");
			},
			itemsRemainingString() {
				return `${this.itemsCompleted} / ${this.totalItems} completed`;
			},
			totalItems() {
				return this.form.reviewItems.length;
			},
			itemsCompleted() {
				return this.form.reviewItems.filter(x=> x.answer !== null && x.answer !== "").length;
			},
		}
	};
</script>

<style scoped>
	.item {
		cursor: pointer;
	}

	.item i {
		font-size: 4em !important;
		border-radius: 50%;
	}

	.very-happy i {
		color: rgb(92, 182, 36);
	}

	.happy i {
		color: rgb(170, 195, 32);
	}

	.neutral i {
		color: rgb(244, 197, 17);
	}

	.sad i {
		color: rgb(246, 142, 31);
	}

	.very-sad i {
		color: rgb(245, 77, 28);
	}

	.tl-red i {
		color: #f44336 !important;
	}

	.tl-amber i {
		color: #ffc107 !important;
	}

	.tl-green i {
		color: #4caf50 !important;
	}

	.question-card-title {
		font-weight: 500;
	}

	.question-card {
		text-align: left;
	}

	.comment-text {
		text-align: left;
	}
	.comment-date {
		text-align: left;
		color: rgba(0, 0, 0, 0.54);
		font-size: 12px;
		font-style: italic;
	}

	.grow-on-hover {
		display: inline-block;
		vertical-align: middle;
		-webkit-transform: perspective(1px) translateZ(0);
		transform: perspective(1px) translateZ(0);
		box-shadow: 0 0 1px rgba(0, 0, 0, 0);
		-webkit-transition-duration: 0.3s;
		transition-duration: 0.3s;
		-webkit-transition-property: transform;
		transition-property: transform;
	}

	.is-not-selected {
		opacity: 0.25;
	}

	.is-selected {
		opacity: 1;
	}
	.is-selected i {
		color: #fff;
	}
	.is-selected.very-happy i {
		background-color: rgb(92, 182, 36);
	}
	.is-selected.happy i {
		background-color: rgb(170, 195, 32);
	}
	.is-selected.neutral i {
		background-color: rgb(244, 197, 17);
	}
	.is-selected.sad i {
		background-color: rgb(246, 142, 31);
	}
	.is-selected.very-sad i {
		background-color: rgb(245, 77, 28);
	}

	.grow-on-hover:hover,
	.grow-on-hover:focus,
	.grow-on-hover:active {
		-webkit-transform: scale(1.2);
		transform: scale(1.2);
		opacity: 1;
	}

	.mouse-disabled-input:hover {
		cursor: not-allowed;
	}

	.answer {
		color: rgba(0, 0, 0, 0.54);
	}

	.further-comment-text {
		color: rgba(0, 0, 0, 0.54);
	}
</style>
